import ForwardToInboxRoundedIcon from "@mui/icons-material/ForwardToInboxRounded";
import { Button } from "@mui/material";
import { useState } from "react";
import TooltipForDisabledAction from "@entrilia/shared/components/TooltipForDisabledAction";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { Contact } from "../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../context/useClientContext";
import InvestorPortalImpersonateButton from "../../investor-portal/InvestorPortalImpersonateButton";
import SendInvitationDialog from "./SendInvitationDialog";
import PortalAccessContext from "./mainInfo/portal-access/PortalAccessContext";
import { getImpersonationGate, getInvitationGate } from "./mainInfo/portal-access/portalAccessModel";

interface Props {
  contact: Contact;
  hasPermissionsToManageContacts: boolean;
  onInvitationSent: () => void;
}

const ContactPortalHeaderActions = ({ contact, hasPermissionsToManageContacts, onInvitationSent }: Props) => {
  const { investorPortalApp } = useClientContext();
  const { portalAccess, isLoading, refetch: refetchPortalAccess } = useDefinedContext(PortalAccessContext);

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  const handleInvitationSent = () => {
    setIsInviteDialogOpen(false);
    refetchPortalAccess();
    onInvitationSent();
  };

  if (!investorPortalApp) {
    return null;
  }

  const impersonationGate = getImpersonationGate({
    hasEmail: Boolean(contact.email),
    hasPortalAccess: contact.hasAccessToInvestorPortal,
  });
  const invitationGate = getInvitationGate({
    hasEmail: Boolean(contact.email),
    hasPortalAccess: contact.hasAccessToInvestorPortal,
    suppressionState: portalAccess?.emailSuppressionState,
    isLoading,
  });
  const isResend = Boolean(contact.invitationSentAt);

  return (
    <>
      <InvestorPortalImpersonateButton
        params={{ target: "Investor", userEmail: contact.email }}
        variant="outlined"
        color="secondary"
        disabled={!impersonationGate.canImpersonate}
        disabledTooltipText={impersonationGate.tooltip}
      />
      {hasPermissionsToManageContacts && (
        <TooltipForDisabledAction title={invitationGate.tooltip} isActionDisabled={!invitationGate.canSend}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ForwardToInboxRoundedIcon />}
            disabled={!invitationGate.canSend}
            onClick={() => setIsInviteDialogOpen(true)}
          >
            {isResend ? "Re-send invitation" : "Send invitation"}
          </Button>
        </TooltipForDisabledAction>
      )}
      <SendInvitationDialog
        open={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
        onInvitationSent={handleInvitationSent}
        contactIds={[contact.id]}
        isResend={isResend}
        description={
          <>
            You&apos;re about to send an invitation to <strong>{contact.name}</strong>.
          </>
        }
      />
    </>
  );
};

export default ContactPortalHeaderActions;
